import React, { useRef, useState } from "react";

import { PortalClassesEnum } from "@/ts/enums/PortalClassesEnum";
import { getTrackingId } from "@/utils/utils";

import { Box, Image, ImageProps, Link, LinkProps } from "@chakra-ui/react";

export interface LogoVariant {
    imageProps: Partial<ImageProps>;
    linkProps?: Partial<LinkProps>;
}

export interface LogoProps extends Omit<ImageProps, "src"> {
    variant: "brandmark" | "inline" | "stacked" | "square";
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    src: string;
    alt?: string;
    href?: string;
    isDarkMint?: boolean;
    backgroundColor?: string;
    linkProps?: Omit<LinkProps, "href">;
}

const LOGO_SIZES = {
    xs: { base: "16px", md: "24px", lg: "32px" },
    sm: { base: "24px", md: "32px", lg: "42px" },
    md: { base: "32px", md: "48px", lg: "56px" },
    lg: { base: "48px", md: "64px", lg: "72px" },
    xl: { base: "64px", md: "80px", lg: "96px" }
};

const LOGO_VARIANTS: Record<string, LogoVariant> = {
    brandmark: {
        imageProps: {
            height: "auto"
        }
    },
    inline: {
        imageProps: {
            pointerEvents: "none",
            userSelect: "none",
            height: "auto"
        },
        linkProps: {
            className: "nav-item"
        }
    },
    stacked: {
        imageProps: {
            pointerEvents: "none",
            userSelect: "none",
            height: "auto"
        }
    },
    square: {
        imageProps: {
            height: "auto"
        }
    }
};

const PADDING_RATIOS = {
    brandmark: 0.5, // image width / 2
    inline: 1 / 6, // image width / 6
    stacked: 0.25, // image width / 4
    square: 0.125 // minimal padding for square variant (width / 8)
};

export const getLogoVariant = (variantClass: string): LogoVariant =>
    LOGO_VARIANTS[variantClass] || LOGO_VARIANTS.brandmark;

export const Logo: React.FC<LogoProps> = ({
    variant,
    size = "md",
    src,
    alt = "Euka Logo",
    href,
    linkProps,
    isDarkMint = true,
    ...props
}) => {
    const [imageWidth, setImageWidth] = useState<number>(0);
    const imageRef = useRef<HTMLImageElement>(null);
    const variantProp = getLogoVariant(variant);
    const logoSize = LOGO_SIZES[size];
    const handleImageLoad = () => {
        if (imageRef.current) {
            setImageWidth(imageRef.current.offsetWidth);
        }
    };

    const getDynamicPadding = () => {
        if (imageWidth === 0) return "8px"; // fallback while image loads

        const ratio = PADDING_RATIOS[variant] || PADDING_RATIOS.brandmark;
        return `${imageWidth * ratio}px`;
    };

    const getContainerProps = () => {
        const dynamicPadding = getDynamicPadding();

        const baseProps = {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center"
        };

        switch (variant) {
            case "brandmark":
                return {
                    ...baseProps,
                    padding: dynamicPadding
                };
            case "inline":
                return {
                    ...baseProps,
                    padding: dynamicPadding
                };
            case "stacked":
                return {
                    ...baseProps,
                    padding: dynamicPadding
                };
            case "square":
                return {
                    ...baseProps,
                    width: logoSize,
                    height: logoSize,
                    bg: "warning.500",
                    _dark: {
                        bg: "warning.500"
                    },
                    padding: dynamicPadding
                };
            default:
                return {
                    ...baseProps,
                    padding: dynamicPadding
                };
        }
    };

    const getImageProps = () => {
        if (variant === "square") {
            return {
                ...variantProp.imageProps,
                width: "100%",
                height: "100%",
                objectFit: "contain" as const,
                ref: imageRef,
                onLoad: handleImageLoad
            };
        }
        return {
            ...variantProp.imageProps,
            width: logoSize,
            ref: imageRef,
            filter: {
                _light: "none",
                _dark: isDarkMint
                    ? "brightness(0) invert(1) sepia(1) saturate(1000%) hue-rotate(40deg)  drop-shadow(0 0 0px #B6DE83)"
                    : "brightness(0) invert(1)"
            },
            onLoad: handleImageLoad
        };
    };

    const logoImage = (
        <Box {...getContainerProps()}>
            <Image src={src} alt={alt} {...getImageProps()} {...props} />
        </Box>
    );

    if (href) {
        return (
            <Link
                href={href}
                {...variantProp.linkProps}
                {...linkProps}
                data-tracking-id={getTrackingId(
                    [PortalClassesEnum.Logo, PortalClassesEnum.Link],
                    "home"
                )}
            >
                {logoImage}
            </Link>
        );
    }

    return logoImage;
};

export default Logo;
